import React from "react";
import { Target, Eye, ShieldCheck, Award, Users, Wrench } from "lucide-react";
import PageHero from "@/components/PageHero";
import { IMG, STATS } from "@/data/site";
import CountUp from "@/components/CountUp";
import { Reveal } from "@/components/Reveal";
import FinalCTA from "@/components/home/FinalCTA";

const values = [
  { icon: ShieldCheck, title: "Engineering First", text: "We diagnose root causes and apply the right system — not quick patches." },
  { icon: Award, title: "Premium Quality", text: "High-grade materials and certified application, every single time." },
  { icon: Users, title: "Customer-First", text: "Transparent pricing, clear timelines and prompt, respectful service." },
  { icon: Wrench, title: "Lasting Warranty", text: "Up to 15 years of documented protection, honoured without excuses." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        title="Protecting Jaipur, One Structure at a Time"
        subtitle="JAL RAKSHAKK is a premium waterproofing brand built on engineering expertise, superior workmanship and a customer-first promise."
        image={IMG.team}
        crumbs={[{ label: "About" }]}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="rounded-lg overflow-hidden border border-slate-200">
              <img src={IMG.team} alt="JAL RAKSHAKK expert team" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              <span className="text-royal text-xs font-semibold tracking-[0.3em] uppercase">Our Story</span>
              <h2 className="font-display text-4xl font-extrabold text-navy-deep mt-3 mb-5">12+ Years of Trusted Protection</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                For over a decade, JAL RAKSHAKK has protected homes, businesses and industrial structures across Jaipur from the silent damage of water. What began as a specialist waterproofing team has grown into one of the city's most trusted names — powered by engineering rigour and an obsession with lasting results.
              </p>
              <p className="text-slate-600 leading-relaxed">
                With 470+ completed projects, 500+ satisfied customers and a warranty of up to 15 years, we're on a mission to become India's No.1 waterproofing company — and we're just getting started.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-navy py-16">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-display text-3xl md:text-4xl font-extrabold text-gradient-blue"><CountUp to={s.value} suffix={s.suffix} /></div>
              <div className="text-slate-300 text-xs mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-soft-grey py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-8">
          <Reveal>
            <div className="bg-white rounded-lg p-8 border border-slate-200 h-full">
              <Target className="h-9 w-9 text-royal" />
              <h3 className="font-display text-2xl font-extrabold text-navy-deep mt-4 mb-2">Our Mission</h3>
              <p className="text-slate-600 leading-relaxed">To protect every home, business and structure from water damage through innovative waterproofing solutions, superior workmanship and customer-first service — ensuring long-lasting peace of mind.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-white rounded-lg p-8 border border-slate-200 h-full">
              <Eye className="h-9 w-9 text-royal" />
              <h3 className="font-display text-2xl font-extrabold text-navy-deep mt-4 mb-2">Our Vision</h3>
              <p className="text-slate-600 leading-relaxed">To become India's most trusted waterproofing company by delivering reliable engineering solutions, exceptional quality and an unmatched customer experience.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal><h2 className="font-display text-4xl font-extrabold text-navy-deep text-center mb-14">What We Stand For</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="bg-soft-grey rounded-lg p-7 border border-slate-200 h-full hover:-translate-y-1 hover:shadow-lg transition-all">
                  <div className="h-12 w-12 rounded-md bg-royal/10 flex items-center justify-center"><v.icon className="h-6 w-6 text-royal" /></div>
                  <h3 className="font-display text-lg font-bold text-navy-deep mt-4">{v.title}</h3>
                  <p className="text-slate-600 text-sm mt-2">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
